// frontend/app/components/lessonPlans/blocks/LessonLoopBlocks.js
//
// The written-work blocks of the Saras Learning Loop: explanation (Explain),
// reflection (Reflect) and exploration_probe (Explore). Like the check blocks,
// each is controlled by `entry` + `onAnswer(block, value)` and never mutates
// what it is handed — the value it writes is always a new object.
//
// None of the three is auto-graded. Explanation and probe are scored by the AI
// evaluator on submit; a reflection is metacognition and is never scored.
//
// The two Grow blocks live in LessonGrowBlocks.js and are re-exported here, so
// the renderer has one import for all five Loop types.

import React, { useCallback, useMemo } from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";

import { getPhaseMeta } from "../lessonPlanTheme";
import { reflectionIsComplete } from "../lessonPlanRules";
import {
  ActionButton,
  AnswerFeedback,
  BlockCard,
  BlockHeading,
  BlockPrompt,
} from "./LessonBlockShell";
import VoiceAnswerControl from "./VoiceAnswerControl";
import {
  AiFeedbackPanel,
  CONFUSION_KEY,
  ConceptTag,
  Disclosure,
  RubricDisclosure,
  countWords,
  loopStyles,
} from "./LessonLoopShell";

export { MasteryMapBlock, NextChallengeBlock } from "./LessonGrowBlocks";

/** Explain phase. Long-form writing, scored by the AI evaluator on submit. */
export const ExplanationBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  onCheck,
  readOnly,
  conceptLabels,
  // Speaking is offered wherever a microphone exists; both are optional, and
  // their absence simply means the block is typed, as before.
  language,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta("explain");
  const value = typeof entry?.value === "string" ? entry.value : "";
  const words = countWords(value);
  const minWords = Number(block.min_words) || 0;
  // Advisory, like every other rule in this runtime: a short answer is flagged,
  // never blocked. A student one word under the target still gets feedback.
  const short = minWords > 0 && words < minWords;
  const submitted = entry?.checked === true;

  return (
    <BlockCard colors={colors} accent={phase.accent}>
      <Text style={[loopStyles.eyebrow, { color: phase.accent }]}>
        {translate("Explain your thinking")}
      </Text>
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>

      <VoiceAnswerControl
        colors={colors}
        accent={phase.accent}
        value={value}
        onChange={(next) => onAnswer(block, next)}
        readOnly={readOnly}
        language={language}
        onEvent={onExplorationEvent}
        t={t}
      />

      <TextInput
        value={value}
        onChangeText={(next) => onAnswer(block, next)}
        editable={!readOnly}
        multiline
        placeholder={block.placeholder || translate("Write your explanation…")}
        placeholderTextColor={colors.textMuted}
        accessibilityLabel={block.prompt}
        style={[
          loopStyles.input,
          loopStyles.longInput,
          {
            backgroundColor: colors.subtleBg,
            borderColor: colors.border,
            color: colors.textPrimary,
          },
        ]}
      />

      <View style={loopStyles.metaRow}>
        <Text
          style={[
            loopStyles.wordCount,
            { color: short ? colors.warning : colors.textMuted },
          ]}
        >
          {minWords > 0
            ? `${words}/${minWords} ${translate("words")}`
            : `${words} ${translate("words")}`}
        </Text>
        {!submitted ? (
          <ActionButton
            colors={colors}
            label={translate("Submit for feedback")}
            icon="send-outline"
            variant="outline"
            disabled={readOnly || words === 0}
            onPress={() => onCheck && onCheck(block)}
          />
        ) : null}
      </View>

      <RubricDisclosure block={block} colors={colors} t={t} />

      {entry?.aiFeedback ? (
        <AiFeedbackPanel colors={colors} feedback={entry.aiFeedback} t={t} />
      ) : submitted ? (
        <AnswerFeedback colors={colors} isCorrect={null} t={t} />
      ) : null}

      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} />
    </BlockCard>
  );
};

/** Reflect phase. Metacognition — recorded for the teacher, never scored. */
export const ReflectionBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  readOnly,
  conceptLabels,
  language,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta("reflect");
  const prompts = useMemo(
    () =>
      (Array.isArray(block.prompts) ? block.prompts : []).filter(
        (prompt) => prompt?.key
      ),
    [block.prompts]
  );
  const value = entry?.value && typeof entry.value === "object" ? entry.value : {};

  // Only an explicit null omits the confusion field; an absent key means the
  // author took the default, which is on.
  const confusionLabel =
    block.confusion_prompt === null
      ? null
      : block.confusion_prompt || translate("Anything still confusing?");

  const write = useCallback(
    (key, text) => onAnswer(block, { ...value, [key]: text }),
    [block, onAnswer, value]
  );

  return (
    <BlockCard colors={colors} accent={phase.accent}>
      <BlockHeading
        colors={colors}
        icon={phase.icon}
        accent={phase.accent}
        title={block.title || translate("Look back")}
      />

      {prompts.map((prompt) => (
        <View key={`${block.id}-${prompt.key}`} style={loopStyles.field}>
          <Text style={[loopStyles.fieldLabel, { color: colors.textSecondary }]}>
            {prompt.label}
          </Text>
          {/* One control per prompt, not one for the block: a single button
              could not know which of several fields the student meant. */}
          <VoiceAnswerControl
            colors={colors}
            accent={phase.accent}
            value={typeof value[prompt.key] === "string" ? value[prompt.key] : ""}
            onChange={(next) => write(prompt.key, next)}
            readOnly={readOnly}
            language={language}
            onEvent={onExplorationEvent}
            t={t}
          />
          <TextInput
            value={typeof value[prompt.key] === "string" ? value[prompt.key] : ""}
            onChangeText={(next) => write(prompt.key, next)}
            editable={!readOnly}
            multiline
            placeholder={prompt.placeholder || translate("Type your answer…")}
            placeholderTextColor={colors.textMuted}
            accessibilityLabel={prompt.label}
            style={[
              loopStyles.input,
              {
                backgroundColor: colors.subtleBg,
                borderColor: colors.border,
                color: colors.textPrimary,
              },
            ]}
          />
        </View>
      ))}

      {confusionLabel ? (
        <View style={loopStyles.field}>
          <Text style={[loopStyles.fieldLabel, { color: colors.teacherTipText }]}>
            {confusionLabel}
          </Text>
          <TextInput
            value={typeof value[CONFUSION_KEY] === "string" ? value[CONFUSION_KEY] : ""}
            onChangeText={(next) => write(CONFUSION_KEY, next)}
            editable={!readOnly}
            multiline
            placeholder={translate("It's fine to leave this empty.")}
            placeholderTextColor={colors.textMuted}
            accessibilityLabel={confusionLabel}
            style={[
              loopStyles.input,
              {
                backgroundColor: colors.teacherTipBg,
                borderColor: colors.teacherTipBorder,
                color: colors.textPrimary,
              },
            ]}
          />
        </View>
      ) : null}

      {reflectionIsComplete(block, value) ? (
        <AnswerFeedback colors={colors} isCorrect={null} t={t} />
      ) : null}

      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} />
    </BlockCard>
  );
};

/** Explore phase. A short "what changed?" beside the model, scored lightly. */
export const ExplorationProbeBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  onCheck,
  onHintRequest,
  readOnly,
  conceptLabels,
  language,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta("explore");
  const value = typeof entry?.value === "string" ? entry.value : "";
  const submitted = entry?.checked === true;

  // Asking for a nudge is exploration evidence in its own right, so revealing
  // the hint reports itself upward.
  const requestHint = useCallback(() => {
    if (onHintRequest) onHintRequest(block.id);
  }, [block.id, onHintRequest]);

  return (
    <BlockCard colors={colors} accent={phase.accent}>
      <Text style={[loopStyles.eyebrow, { color: phase.accent }]}>
        {translate("Quick probe")}
      </Text>
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>

      <VoiceAnswerControl
        colors={colors}
        accent={phase.accent}
        value={value}
        onChange={(next) => onAnswer(block, next)}
        readOnly={readOnly}
        language={language}
        onEvent={onExplorationEvent}
        t={t}
      />

      <View style={styles.probeRow}>
        <TextInput
          value={value}
          onChangeText={(next) => onAnswer(block, next)}
          editable={!readOnly}
          placeholder={block.placeholder || translate("A sentence is plenty…")}
          placeholderTextColor={colors.textMuted}
          accessibilityLabel={block.prompt}
          style={[
            loopStyles.input,
            styles.probeInput,
            {
              backgroundColor: colors.subtleBg,
              borderColor: colors.border,
              color: colors.textPrimary,
            },
          ]}
        />
        {!submitted ? (
          <ActionButton
            colors={colors}
            label={translate("Submit")}
            icon="send-outline"
            variant="outline"
            disabled={readOnly || value.trim().length === 0}
            onPress={() => onCheck && onCheck(block)}
          />
        ) : null}
      </View>

      {block.hint ? (
        <View style={styles.hintWrap}>
          <Disclosure
            colors={colors}
            accent={colors.accentPrimary}
            label={translate("Need a nudge?")}
            onOpen={requestHint}
          >
            <Text style={[styles.hintText, { color: colors.textSecondary }]}>
              {block.hint}
            </Text>
          </Disclosure>
        </View>
      ) : null}

      {entry?.aiFeedback ? (
        <AiFeedbackPanel colors={colors} feedback={entry.aiFeedback} t={t} />
      ) : submitted ? (
        <AnswerFeedback colors={colors} isCorrect={null} t={t} />
      ) : null}

      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} />
    </BlockCard>
  );
};

const styles = StyleSheet.create({
  probeRow: { flexDirection: "row", alignItems: "flex-start" },
  probeInput: { flex: 1, minHeight: 44, marginRight: 8 },
  hintWrap: { marginTop: 10 },
  hintText: { fontSize: 13, lineHeight: 20, marginTop: 6, marginLeft: 20 },
});
